import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DataTable, type DataColumn } from '@/components/data/DataTable';
import { DateTime } from '@/components/data/DateTime';
import { AuditChip } from '@/components/audit/AuditChip';
import { ModerateReviewDialog } from '@/features/commerce-ops/ModerateReviewDialog';
import {
  REVIEW_STATES,
  reviewStateTone,
} from '@/features/commerce-ops/orderState';
import {
  reviewStateSchema,
  type ProductReview,
  type ReviewListFilters,
  type ReviewState,
} from '@/schemas/admin/commerce/reviews';
import { useReviews } from '@/queries/commerceOps';
import { useApiError } from '@/lib/useApiError';

const searchSchema = z.object({
  state: reviewStateSchema.optional(),
});

type ReviewsSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute('/_authed/commerce/reviews')({
  validateSearch: searchSchema,
  component: ReviewsScreen,
});

function ReviewsScreen() {
  const { t } = useTranslation('commerce');
  const search = Route.useSearch();
  const navigate = useNavigate();
  const resolveError = useApiError();

  const filters: ReviewListFilters = React.useMemo(() => {
    const out: ReviewListFilters = { limit: 100 };
    if (search.state) out.state = search.state;
    else out.state = 'pending';
    return out;
  }, [search.state]);

  const list = useReviews(filters);
  const [moderating, setModerating] = React.useState<ProductReview | null>(null);

  const data = list.data ?? [];

  function setSearchParam<K extends keyof ReviewsSearch>(key: K, value: ReviewsSearch[K]) {
    const next: ReviewsSearch = { ...search, [key]: value };
    void navigate({ to: '/commerce/reviews', search: next, replace: true });
  }

  const columns: readonly DataColumn<ProductReview>[] = [
    {
      id: 'product',
      header: t('reviews.table.product'),
      cell: (r) => <span className="font-medium">{r.product_name ?? '—'}</span>,
      sortBy: (r) => r.product_name ?? '',
    },
    {
      id: 'reviewer',
      header: t('reviews.table.reviewer'),
      cell: (r) => (
        <span dir="ltr" className="font-mono text-xs text-[color:var(--color-fg-muted)]">
          {r.reviewer_handle_masked ?? '—'}
        </span>
      ),
    },
    {
      id: 'rating',
      header: t('reviews.table.rating'),
      align: 'center',
      cell: (r) => (
        <span aria-label={`${r.rating} of 5`} className="font-mono">
          {'★'.repeat(r.rating)}
          <span className="text-[color:var(--color-fg-subtle)]">{'☆'.repeat(5 - r.rating)}</span>
        </span>
      ),
      sortBy: (r) => r.rating,
    },
    {
      id: 'body',
      header: t('reviews.table.body'),
      cell: (r) => (
        <span className="line-clamp-2 max-w-md text-sm text-[color:var(--color-fg-muted)]">
          {r.body ?? '—'}
        </span>
      ),
    },
    {
      id: 'state',
      header: t('reviews.tableState', { defaultValue: 'State' }),
      cell: (r) => <Badge tone={reviewStateTone(r.state)}>{t(`reviews.states.${r.state}`)}</Badge>,
      sortBy: (r) => r.state,
    },
    {
      id: 'submitted',
      header: t('reviews.table.submitted'),
      cell: (r) => <DateTime value={r.created_at ?? null} />,
      sortBy: (r) => r.created_at ?? '',
    },
    {
      id: 'actions',
      header: t('reviews.table.actions'),
      cell: (r) =>
        r.state === 'pending' ? (
          <Button size="sm" variant="secondary" onClick={() => setModerating(r)}>
            {t('reviews.moderate.trigger')}
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{t('reviews.title')}</h1>
          <p className="max-w-3xl text-sm text-[color:var(--color-fg-muted)]">{t('reviews.subtitle')}</p>
        </div>
        <AuditChip />
      </header>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-wrap items-end gap-3">
            <div className="basis-56">
              <label htmlFor="reviews-state" className="sr-only">
                {t('reviews.filters.stateAll')}
              </label>
              <select
                id="reviews-state"
                value={search.state ?? 'pending'}
                onChange={(e) =>
                  setSearchParam('state', (e.target.value || undefined) as ReviewState | undefined)
                }
                className="block h-11 w-full rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)]"
              >
                <option value="">{t('reviews.filters.stateAll')}</option>
                {REVIEW_STATES.map((s) => (
                  <option key={s} value={s}>
                    {t(`reviews.states.${s}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <DataTable<ProductReview>
            data={data}
            columns={columns}
            rowKey={(r) => r.id}
            loading={list.isLoading}
            error={list.error ? resolveError(list.error) : null}
            empty={{
              title: t('reviews.empty.title'),
              body: t('reviews.empty.body'),
            }}
            initialSort={{ id: 'submitted', direction: 'desc' }}
          />
        </CardContent>
      </Card>

      <ModerateReviewDialog
        review={moderating}
        open={Boolean(moderating)}
        onOpenChange={(open) => {
          if (!open) setModerating(null);
        }}
      />
    </div>
  );
}
